import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import {
  MatLegacySelect as MatSelect,
  MatLegacySelectChange as MatSelectChange,
} from '@angular/material/legacy-select';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, from } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { RfqLotFormComponent } from '@app/modules/purchase-orders/rfq-lot-form/rfq-lot-form.component';

import { ILot } from '../../../lot/interfaces/lot.interface';
import { IRfqLot } from '../../../rfq/interfaces/rfq-lot.interface';
import { trackByControlValue } from '../../../shared/util';
import { IUnitOfMeasure } from '../../../unit-of-measure';

@Component({
  selector: 'lum-assembly-order-form-table',
  templateUrl: './assembly-order-form-table.component.html',
  styleUrls: ['./assembly-order-form-table.component.scss'],
})
export class AssemblyOrderFormTableComponent implements OnInit {
  @Input()
  form :UntypedFormGroup;

  @Input()
  rows :UntypedFormArray;

  @Input()
  items :any[] = [];

  @Input()
  warehouses :any[];

  @Input()
  locations :any[];

  @Input()
  lots :any[] = [];

  @Input()
  hasLotTracking :boolean = false;

  @Input()
  loading :boolean = false;

  @Input()
  rfqActualUom :IUnitOfMeasure | null = null;

  @Output()
  updateQuantityEvent = new EventEmitter<{
    index :number;
    value :number;
  }>();

  @Output()
  selectWarehouseEvent = new EventEmitter<{
    id :number;
    index :number;
    value :number;
  }>();

  @Output()
  selectLocationEvent = new EventEmitter<{
    id :number;
    index :number;
    value :number;
  }>();

  @Output()
  createNewLotEvent = new EventEmitter<{
    index :number;
    createdLotId :number;
    lineLots :any[];
  }>();

  @Output()
  searchLotNumberEvent = new EventEmitter<{
    index :number;
    locationId :number;
    rfqId :number;
    searchValue :string;
  }>();

  @ViewChild('lotMatSelect') lotMatSelect :MatSelect;

  trackByControlValue = trackByControlValue;
  checkedAllItems :boolean = false;
  updateQuantitySubject = new Subject<{ event :any; index :number }>();
  isShowingNotes :boolean = true;

  searchSubject = new Subject<{ index :number; value :any }>();

  private onDestroy$ = new Subject();

  constructor(private modalSvc :NgbModal) {}

  ngOnInit() :void {
    this.updateQuantitySubject
      .pipe(debounceTime(500))
      .subscribe(({ event, index }) => {
        this.onUpdateQuantityEvent(event, index);
      });

    this.searchSubject.pipe(debounceTime(500)).subscribe(({ index, value }) => {
      this.handleSearch(index, value);
    });

    this.displayFnLot = this.displayFnLot.bind(this);
  }

  onInputLotChange(index :number, value :string) :void {
    this.searchSubject.next({ index, value });
  }

  onSelectWarehouseEvent(event :MatSelectChange, index :number) :void {
    const row = this.rows.controls[index]?.value;
    this.selectWarehouseEvent.emit({
      id: row.rfqId,
      index: index,
      value: event?.value,
    });
  }

  onSelectLocationEvent(event :MatSelectChange, index :number) :void {
    const row = this.rows.controls[index]?.value;
    this.selectLocationEvent.emit({
      id: row.rfqId,
      index: index,
      value: event?.value,
    });
  }

  onInputEvent(event :any, index :number) :void {
    this.updateQuantitySubject.next({ event, index });
  }

  onUpdateQuantityEvent(event :Event, index :number) :void {
    const value = (event.target as HTMLInputElement).value;
    this.updateQuantityEvent.emit({
      index: index,
      value: Number(value),
    });
  }

  onCreateNewLot(index :number) :void {
    // Then close the mat-select programmatically
    if (this.lotMatSelect) {
      this.lotMatSelect.close();
    }
    const row = this.rows.controls[index]?.value;
    const modalRef = this.modalSvc.open(RfqLotFormComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.rfqId = row.rfqId;
    modalRef.componentInstance.quantityOriginal = row.plannedBaseQty || 0;

    from(modalRef.result)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((selectedRfqLot) => {
        if (selectedRfqLot) {
          this.pushCreatedLot(selectedRfqLot, index);
        }
      });
  }

  displayFnLot(option :any) :string {
    if (!option) return '';
    if (typeof option === 'object' && option.lotNumber) {
      return option.lotNumber;
    }
    const foundLot = this.findLotById(option); // Assumes lot is an ID here

    return foundLot ? foundLot.lotNumber : '';
  }

  onSelectLotEvent(event :MatAutocompleteSelectedEvent, index :number) :void {
    const selectedLotId = event.option.value;

    this.rows
      .at(index)
      .get('lotId')
      .patchValue(selectedLotId, { emitEvent: false });
  }

  findLotById(lotId :number) :ILot | null {
    for (const lotGroup of this.lots) {
      if (lotGroup) {
        for (const key in lotGroup) {
          const foundLot = lotGroup[key].find((l) => l.id === lotId);
          if (foundLot) {
            return foundLot;
          }
        }
      }
    }

    return null;
  }

  handleSearch(index :number, value :string) :void {
    const rfqIdControlValue = this.rows.controls[index]?.get('rfqId')?.value;
    const locationIdControlValue =
      this.rows.controls[index]?.get('locationId')?.value;

    this.searchLotNumberEvent.emit({
      index: index,
      locationId: locationIdControlValue,
      rfqId: rfqIdControlValue,
      searchValue: value,
    });
  }

  handleToggleNotesClick(e :Event) {
    e.preventDefault();

    this.#toggleNotes();
  }

  #toggleNotes(targetState ?:boolean) {
    this.isShowingNotes =
      typeof targetState === 'boolean' ? targetState : !this.isShowingNotes;
  }

  private pushCreatedLot(selectedRfqLot :IRfqLot, index :number) :void {
    const row = this.rows.controls[index]?.value;
    this.rows.controls[index].get('lotId')?.patchValue(selectedRfqLot?.id);
    this.lots[row.rfqId]?.[row.locationId].push({
      id: selectedRfqLot?.id,
      lotNumber: selectedRfqLot?.lot_number,
      manufacturerCode: selectedRfqLot?.manufacturer_code,
      originalQuantity: selectedRfqLot?.quantity_original,
      productionDate: selectedRfqLot?.production_date,
      rfqId: selectedRfqLot?.rfq_id,
      updatedAt: selectedRfqLot?.updated_at,
      createdAt: selectedRfqLot?.created_at,
      expiryDate: selectedRfqLot?.expiry_date,
    });
    this.createNewLotEvent.emit({
      index: index,
      createdLotId: selectedRfqLot?.id,
      lineLots: this.lots,
    });
  }
}
